import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { AlertTriangle, FileText, FileDown, PauseCircle, Timer, CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min ${String(sec).padStart(2, "0")}s`;
  if (m > 0) return `${m}min ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

export default function AdminMaintenance() {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [lastPause, setLastPause] = useState(null);
  // Programmation à l'avance
  const [scheduled, setScheduled] = useState(null);
  const [schedStartsLocal, setSchedStartsLocal] = useState("");
  const [schedEndsLocal, setSchedEndsLocal] = useState("");
  const [schedNote, setSchedNote] = useState("");
  const [schedSaving, setSchedSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/system/maintenance");
      setEnabled(!!data.enabled);
      setMessage(data.message || "");
      setStartedAt(data.startedAt || null);
    } finally { setLoaded(true); }
    try {
      const { data: s } = await api.get("/system/scheduled-maintenance");
      setScheduled(s?.active ? s : null);
    } catch { setScheduled(null); }
  };

  useEffect(() => { load(); }, []);

  // Tick toutes les secondes pour mettre à jour le compteur en temps réel.
  useEffect(() => {
    if (!enabled || !startedAt) { setElapsed(0); return; }
    const tick = () => {
      try {
        const startMs = new Date(startedAt).getTime();
        setElapsed(Math.floor((Date.now() - startMs) / 1000));
      } catch { setElapsed(0); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enabled, startedAt]);

  const save = async (nextEnabled) => {
    setSaving(true);
    try {
      const resp = await api.put("/admin/maintenance", { enabled: nextEnabled ?? enabled, message: message || null });
      const isOn = nextEnabled ?? enabled;
      if (resp.data?.pauseApplied) {
        const s = resp.data.pauseApplied;
        setLastPause(s);
        toast.success(
          `Site accessible à nouveau. Compteurs décalés de ${formatDuration(s.pauseDurationSeconds || 0)} ` +
          `(${s.queues_additionalExpiresAt || 0} files + ${s.commerces_subscriptionEndDate || 0} abonnements).`,
          { duration: 8000 },
        );
      } else {
        toast.success(isOn ? "Maintenance activée — compteurs des files suspendus" : "Site accessible à nouveau");
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  // Convertit la valeur d'un <input type="datetime-local"> (heure locale
  // sans fuseau) en ISO 8601 UTC pour l'API.
  const localToIso = (local) => {
    if (!local) return null;
    const d = new Date(local);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const scheduleMaintenance = async () => {
    const startsIso = localToIso(schedStartsLocal);
    const endsIso = localToIso(schedEndsLocal);
    if (!startsIso || !endsIso) {
      toast.error("Veuillez saisir une date de début et de fin.");
      return;
    }
    if (new Date(startsIso) <= new Date()) {
      toast.error("La date de début doit être dans le futur.");
      return;
    }
    if (new Date(endsIso) <= new Date(startsIso)) {
      toast.error("La date de fin doit être après la date de début.");
      return;
    }
    setSchedSaving(true);
    try {
      const { data } = await api.post("/admin/scheduled-maintenance", {
        startsAt: startsIso, endsAt: endsIso, note: schedNote || null,
      });
      toast.success(`Maintenance programmée — ${data.emailsSent} email(s) envoyé(s) aux établissements.`, { duration: 6000 });
      setSchedStartsLocal(""); setSchedEndsLocal(""); setSchedNote("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de programmation.");
    } finally { setSchedSaving(false); }
  };

  const cancelScheduled = async () => {
    if (!window.confirm("Annuler la maintenance programmée ?")) return;
    setSchedSaving(true);
    try {
      await api.delete("/admin/scheduled-maintenance");
      toast.success("Programmation annulée.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    } finally { setSchedSaving(false); }
  };

  if (!loaded) return <div className="text-subtle">Chargement…</div>;

  return (
    <div data-testid="admin-maintenance-page">
      <h2 className="font-display text-2xl font-black text-ink mb-4">Mode maintenance</h2>

      <div className={`border-2 p-4 mb-6 ${enabled ? "border-destructive bg-destructive/5" : "border-border bg-surface/40"}`} data-testid="maintenance-status">
        <div className="flex items-center gap-3">
          <AlertTriangle size={20} className={enabled ? "text-destructive" : "text-subtle"} />
          <div className="flex-1">
            <p className="font-bold text-ink">{enabled ? "Site actuellement en maintenance" : "Site accessible normalement"}</p>
            <p className="text-xs text-subtle mt-0.5">
              {enabled
                ? "Les visiteurs et établissements voient le message ci-dessous. Vous (admin) gardez accès à la console."
                : "Activez le mode maintenance pour bloquer temporairement l'accès à la plateforme."}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={(v) => { setEnabled(v); save(v); }} disabled={saving} data-testid="maintenance-toggle" />
        </div>
      </div>

      {/* Bloc d'information : suspension automatique des décomptes pendant
          la maintenance. Affiché en permanence pour la transparence côté
          admin. Affiche le compteur en direct quand la maintenance est ON. */}
      <div
        className={`border-2 p-4 mb-6 ${enabled ? "border-amber-500 bg-amber-50" : "border-border bg-surface/40"}`}
        data-testid="maintenance-pause-info"
      >
        <div className="flex items-start gap-3">
          <PauseCircle size={20} className={enabled ? "text-amber-700" : "text-subtle"} />
          <div className="flex-1">
            <p className="font-bold text-ink">Suspension automatique des décomptes</p>
            <p className="text-xs text-subtle mt-1 leading-relaxed">
              Pendant toute la durée de la maintenance, les compteurs des
              files d'attente (validité 24 h des files 4,90 € / 7,90 €),
              les périodes d'essai et les abonnements mensuels sont
              <strong> automatiquement suspendus</strong>. À la remise en
              service, tous les compteurs reprennent exactement où ils
              s'étaient arrêtés (aucune minute perdue).
            </p>
            {enabled && (
              <div className="mt-3 flex items-center gap-2 bg-white border border-amber-300 px-3 py-2 rounded-md" data-testid="maintenance-elapsed-counter">
                <Timer size={16} className="text-amber-700" />
                <span className="text-xs text-subtle">Maintenance en cours depuis :</span>
                <span className="font-mono font-bold text-ink text-sm" data-testid="maintenance-elapsed-value">
                  {formatDuration(elapsed)}
                </span>
              </div>
            )}
            {lastPause && !enabled && (
              <div className="mt-3 bg-white border border-emerald-300 px-3 py-2 rounded-md text-xs" data-testid="maintenance-last-pause">
                <p className="font-bold text-ink mb-1">Dernière pause appliquée :</p>
                <ul className="text-subtle space-y-0.5">
                  <li>• Durée totale : <strong>{formatDuration(lastPause.pauseDurationSeconds || 0)}</strong></li>
                  <li>• Files supplémentaires décalées : <strong>{lastPause.queues_additionalExpiresAt ?? 0}</strong></li>
                  <li>• Abonnements décalés : <strong>{lastPause.commerces_subscriptionEndDate ?? 0}</strong></li>
                  <li>• Périodes d'essai décalées : <strong>{lastPause.commerces_trialEndDate ?? 0}</strong></li>
                  <li>• Programmations d'ouverture décalées : <strong>{lastPause.queues_scheduledOpenAt ?? 0}</strong></li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3 max-w-xl">
        <div>
          <Label>Message affiché aux visiteurs</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder="Ex : Mise à jour en cours. Nous revenons dans quelques instants."
            className="mt-1.5 resize-none"
            data-testid="maintenance-message-input"
          />
          <p className="text-xs text-subtle mt-1">{message.length}/500</p>
        </div>
        <Button onClick={() => save()} disabled={saving} className="bg-brand hover:bg-brand-hover text-white rounded-md h-11 px-6" data-testid="maintenance-save-btn">
          Enregistrer le message
        </Button>
      </div>

      {/* === Programmer une maintenance à l'avance === */}
      <div className="mt-10 border border-border bg-surface/40 p-4 max-w-xl" data-testid="scheduled-maintenance-block">
        <h3 className="font-display text-lg font-black text-ink mb-2 flex items-center gap-2">
          <CalendarClock size={18} /> Programmer une maintenance
        </h3>
        <p className="text-xs text-subtle mb-4 leading-relaxed">
          Annoncez une maintenance à l'avance pour prévenir les établissements
          par email et afficher une bannière publique sur le site.
          À l'heure programmée, le mode maintenance s'active <strong>automatiquement</strong>,
          puis se désactive à l'heure de fin (compteurs décalés de la durée
          exacte d'indisponibilité).
        </p>

        {scheduled ? (
          <div className="bg-white border-2 border-blue-300 rounded-md p-4" data-testid="scheduled-maintenance-current">
            <p className="font-bold text-ink mb-2">Maintenance actuellement programmée :</p>
            <ul className="text-xs text-subtle space-y-1 mb-3">
              <li>• Début : <strong className="text-ink">{new Date(scheduled.startsAt).toLocaleString("fr-FR")}</strong></li>
              <li>• Fin : <strong className="text-ink">{new Date(scheduled.endsAt).toLocaleString("fr-FR")}</strong></li>
              {scheduled.note && <li>• Message : <em>{scheduled.note}</em></li>}
              <li>• Emails envoyés : <strong>{scheduled.emailsSent}</strong></li>
              <li>• Auto-activée : <strong>{scheduled.autoActivated ? "oui" : "pas encore"}</strong></li>
            </ul>
            <Button
              variant="outline"
              onClick={cancelScheduled}
              disabled={schedSaving}
              className="border-destructive text-destructive hover:bg-destructive hover:text-white h-9 px-3 text-xs"
              data-testid="cancel-scheduled-maintenance-btn"
            >
              <Trash2 size={14} className="mr-1.5" />
              Annuler la programmation
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="sched-start">Début (heure locale)</Label>
              <Input
                id="sched-start"
                type="datetime-local"
                value={schedStartsLocal}
                onChange={(e) => setSchedStartsLocal(e.target.value)}
                className="mt-1"
                data-testid="scheduled-maintenance-start-input"
              />
            </div>
            <div>
              <Label htmlFor="sched-end">Fin (heure locale)</Label>
              <Input
                id="sched-end"
                type="datetime-local"
                value={schedEndsLocal}
                onChange={(e) => setSchedEndsLocal(e.target.value)}
                className="mt-1"
                data-testid="scheduled-maintenance-end-input"
              />
            </div>
            <div>
              <Label htmlFor="sched-note">Message d'accompagnement (facultatif)</Label>
              <Textarea
                id="sched-note"
                value={schedNote}
                onChange={(e) => setSchedNote(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ex : Mise à niveau de l'infrastructure. Service indisponible pendant ~1h."
                className="mt-1 resize-none"
                data-testid="scheduled-maintenance-note-input"
              />
              <p className="text-xs text-subtle mt-1">{schedNote.length}/500</p>
            </div>
            <Button
              onClick={scheduleMaintenance}
              disabled={schedSaving}
              className="bg-brand hover:bg-brand-hover text-white rounded-md h-11 px-6"
              data-testid="schedule-maintenance-btn"
            >
              <CalendarClock size={16} className="mr-2" />
              Programmer + envoyer les emails
            </Button>
            <p className="text-[11px] text-subtle italic">
              Un email d'annonce sera envoyé immédiatement à tous les
              établissements vérifiés.
            </p>
          </div>
        )}
      </div>

      {/* Spec utilisateur Feb 2026 — accès direct au guide de déploiement
         BypassGo (architecture, variables d'env, prérequis Mongo/Stripe/
         Resend, procédure de migration hors Emergent, etc.). Doc archivée
         dans /app/docs/ et téléchargeable au format MD ou PDF. */}
      <div className="mt-10 border border-border bg-surface/40 p-4 max-w-xl" data-testid="deployment-docs-block">
        <h3 className="font-display text-lg font-black text-ink mb-2 flex items-center gap-2">
          <FileText size={18} /> Documentation de déploiement
        </h3>
        <p className="text-xs text-subtle mb-3 leading-relaxed">
          Guide complet pour archiver et migrer BypassGo hors infrastructure
          Emergent (architecture, variables <code>.env</code>, prérequis
          MongoDB / Stripe / Resend, procédures de sauvegarde, déploiement
          backend &amp; frontend, dépendances, services externes, checklist
          de mise en route).
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/deployment-guide.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const res = await api.get("/admin/deployment-guide.pdf", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const now = new Date();
                a.download = `BypassGo_Deployment_Guide_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erreur de téléchargement.");
              }
            }}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-brand hover:bg-brand-hover text-white text-xs font-bold uppercase tracking-wider rounded-md"
            data-testid="download-deployment-guide-pdf"
          >
            <FileDown size={12} /> Télécharger en PDF
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/deployment-guide.md`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const res = await api.get("/admin/deployment-guide.md", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "text/markdown;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const now = new Date();
                a.download = `BypassGo_Deployment_Guide_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}.md`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erreur de téléchargement.");
              }
            }}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-white hover:border-ink text-ink border border-border text-xs font-bold uppercase tracking-wider rounded-md"
            data-testid="download-deployment-guide-md"
          >
            <FileDown size={12} /> Télécharger en Markdown
          </a>
        </div>
        <p className="text-[10px] text-subtle mt-2 italic">
          Version actuelle : 2026-02-08 (post-migration Stripe SDK natif).
          Source archivée dans <code>/app/docs/BypassGo_Deployment_Guide.md</code>.
        </p>
      </div>

      {/* Spec utilisateur Feb 2026 — accès direct à l'archive INPI
         (code source rédacté + documentation + captures d'écran + manifest
         + preuves techniques). Méta-archive auto-générée par le script
         /app/docs/exports/_build_inpi_archive.py. */}
      <div className="mt-6 border border-border bg-surface/40 p-4 max-w-xl" data-testid="inpi-archive-block">
        <h3 className="font-display text-lg font-black text-ink mb-2 flex items-center gap-2">
          <FileText size={18} /> Archive INPI (preuve d'antériorité)
        </h3>
        <p className="text-xs text-subtle mb-3 leading-relaxed">
          Méta-archive ZIP complète prête pour un dépôt INPI : code source
          (avec secrets rédactés), documentation de déploiement (MD + PDF),
          templates <code>.env</code>, captures d'écran de tous les écrans
          (visiteur / établissement / admin / paiements), échantillon d'export
          comptable, preuve technique de suppression de
          <code>emergentintegrations</code> et manifest SHA-256.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/inpi-archive.zip`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const res = await api.get("/admin/inpi-archive.zip", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/zip" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const now = new Date();
                a.download = `BypassGo_INPI_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Archive non disponible.");
              }
            }}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-brand hover:bg-brand-hover text-white text-xs font-bold uppercase tracking-wider rounded-md"
            data-testid="download-inpi-archive"
          >
            <FileDown size={12} /> Méta-archive INPI (ZIP)
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/source-archive.zip`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const res = await api.get("/admin/source-archive.zip", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/zip" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const now = new Date();
                a.download = `BypassGo_SourceCode_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Archive non disponible.");
              }
            }}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-white hover:border-ink text-ink border border-border text-xs font-bold uppercase tracking-wider rounded-md"
            data-testid="download-source-archive"
          >
            <FileDown size={12} /> Code source seul (ZIP)
          </a>
        </div>
        <p className="text-[10px] text-subtle mt-2 italic leading-relaxed">
          Toutes les clés API, mots de passe et secrets sont automatiquement
          rédactés avant inclusion dans l'archive. Régénérable via
          <code>python3 /app/docs/exports/_build_inpi_archive.py</code>.
        </p>
      </div>
    </div>
  );
}
